import React, { useState, useEffect, useCallback, useMemo, startTransition, memo } from 'react';
import { Plus, PlusCircle, Search, Package, Edit2, Trash2, RotateCcw, LifeBuoy, Flame, Heart, Radio, MapPin, Download, Lock, Shield, BookOpen, Info, ChevronDown, ChevronRight, History, Clock, Loader2, Cog, Zap, Droplets, Anchor, Settings, AlertTriangle } from 'lucide-react';
// FT-358: middle-slot actions registered via context; the shell owns the bar.
import { useSetBottomBarActions } from '../../../../contexts/MobileBottomBarActionsContext';
import Dropdown from '../../../common/Dropdown';
// Removed Catalyst Input import - using regular HTML input for better visibility
import AddEquipmentView from './AddEquipmentView';
import EquipmentDetailModal from './EquipmentDetailModal';
import EquipmentDetailView from './EquipmentDetailView';
import EquipmentFamiliesView from './EquipmentFamiliesView';
import ChangeLogViewer from './ChangeLogViewer';
import EquipmentRegisterEmptyState from '../../../common/EquipmentRegisterEmptyState';
import { loadUserEquipmentSelections } from '../../../../services/equipment/selectionTransformer';
import { useVessel } from 'contexts/VesselContext';
import { useCustomDialogs } from '../../../../hooks/useCustomDialogs';
import { useAuditTrail } from '../../../../hooks/useAuditTrail';
import { useEquipmentRegister } from '../../../../hooks/domain/useEquipmentRegister';
import { devLog } from '../../../../utils/devLog';
import { hasGuidanceForRow } from '../../../../helpers/equipmentGuidanceUtils';
import DomainPackBridge from '../../../../bridge/DomainPackBridge';
import vesselConfigStore from '../../../../state/vesselConfig.store';
import { LocationChip, StatusChip } from '../../common/Chips';
import { downloadEquipmentCSV } from '../../../../utils/equipmentExportCSV';
import { downloadEquipmentPDF } from '../../../../utils/equipmentExportPDF';
import { calculateEquipmentStatus } from '../../../../utils/equipmentJobLinking';
// FT-48: Removed getMaintenanceSchedule config import - now reading from storage
import { normalizeDomain, DOMAIN_LABELS } from '../../../tailwind/settings/tabs/utils/equipmentSelectionHelpers';
import { calculateEquipmentServiceStatus } from '../../../../services/equipmentServiceCalculator';
import { toOrgScopedKey } from '../../../../lib/storage';

// FT-189: Centralized equipment name resolution
import { getEquipmentDisplayName } from '../../../../services/equipment/utils/equipmentRow';

// Phase 3C Performance Fix: Priority cache for equipment sorting
const priorityCache = new Map();

// Helper to normalize location display
const normalizeLocation = (location) => {
  if (!location) return 'General';
  const normalized = location.replace(/_/g, ' ');
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
};

// Next Check Badge Component
const NextCheckBadge = ({ nextCheckDue }) => {
  if (!nextCheckDue) return null;
  
  const dueDate = new Date(nextCheckDue);
  const today = new Date();
  const diffTime = dueDate.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  let badgeClass, text;
  
  if (diffDays < 0) {
    // Overdue
    const overdueDays = Math.abs(diffDays);
    badgeClass = 'bg-red-100 text-red-800';
    text = `Overdue ${overdueDays} day${overdueDays !== 1 ? 's' : ''}`;
  } else if (diffDays === 0) {
    // Due today
    badgeClass = 'bg-red-100 text-red-800';
    text = 'Due today';
  } else if (diffDays <= 7) {
    // Due soon
    badgeClass = 'bg-amber-100 text-amber-800';
    text = `Due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  } else if (diffDays <= 30) {
    // Due this month
    badgeClass = 'bg-yellow-100 text-yellow-800';
    text = `Due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  } else {
    // Due in future
    badgeClass = 'bg-green-100 text-green-800';
    text = dueDate.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'short',
      year: diffDays > 365 ? 'numeric' : undefined
    });
  }
  
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${badgeClass}`}>
      {text}
    </span>
  );
};

// FT-16: Mobile Equipment Card Component - memoized for performance
// Touch targets: min 44px, larger text for readability
const EquipmentCard = memo(({ item, onView, maintenanceJobs, maintenanceHistory }) => {
  const getCategoryIcon = (category) => {
    const icons = {
      'FFE': <Flame className="h-6 w-6 text-cyan-600" />,
      'LSA': <LifeBuoy className="h-6 w-6 text-cyan-600" />,
      'NAVIGATION': <MapPin className="h-6 w-6 text-cyan-600" />,
      'GMDSS': <Radio className="h-6 w-6 text-cyan-600" />,
      'MACHINERY': <Cog className="h-6 w-6 text-cyan-600" />,
      'MEDICAL': <Heart className="h-6 w-6 text-cyan-600" />,
      'ELECTRICAL': <Zap className="h-6 w-6 text-cyan-600" />,
      'ENVIRONMENTAL': <Droplets className="h-6 w-6 text-cyan-600" />,
      'OPERATIONS': <Anchor className="h-6 w-6 text-cyan-600" />
    };
    return icons[category?.toUpperCase()] || <Package className="h-6 w-6 text-cyan-600" />;
  };

  // Use smart status calculation
  const { status: serviceStatus, worstJob } = calculateEquipmentStatus(
    item,
    maintenanceJobs || [],
    maintenanceHistory || {}
  );

  return (
    <div
      className="bg-white rounded-xl shadow-sm ring-1 ring-border p-4 cursor-pointer hover:shadow-md transition-shadow min-h-[72px] active:bg-gray-50"
      onClick={onView}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start flex-1 min-w-0">
          <div className="mr-3 mt-0.5 flex-shrink-0">{getCategoryIcon(item.category || item.domain)}</div>
          <div className="flex-1 min-w-0">
            {/* FT-16: Larger text on mobile for readability - FT-189: Centralized name helper */}
            <h4 className="text-base font-semibold text-foreground truncate leading-snug">
              {getEquipmentDisplayName(item)}
              {item.source === 'added' && item.instanceOf && (
                <span className="ml-2 text-xs text-muted-foreground">(Unit {item.unitNumber || '2'})</span>
              )}
            </h4>
            {/* FT-16: Essential info - always visible */}
            <div className="mt-1 text-sm text-muted-foreground">
              {item.category?.toUpperCase() || 'Other'} • {(item.source === 'requirements' || item.sourceRequirementId) ? 'Required' : 'Added'}
            </div>
            {/* Next Check Due - Most Important Info */}
            <div className="mt-2">
              {item.nextCheckDue ? (
                <NextCheckBadge nextCheckDue={item.nextCheckDue} />
              ) : (
                <span className="inline-flex items-center rounded-full px-2 py-1 text-xs bg-gray-100 text-gray-600">
                  No service history
                </span>
              )}
            </div>

            {/* FT-16: Secondary Info - simplified for mobile */}
            <div className="mt-2 text-xs text-muted-foreground leading-relaxed">
              {item.location ? item.location.charAt(0).toUpperCase() + item.location.slice(1).replace(/_/g, ' ') : 'General'}
              {item._lastServiceDate && (
                <span> • Last: {new Date(item._lastServiceDate).toLocaleDateString('en-GB', {
                  day: 'numeric', month: 'short'
                })}</span>
              )}
            </div>
          </div>
        </div>
        {/* FT-16: Chevron indicator for tappable cards */}
        <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0 ml-2 mt-2" />
      </div>
    </div>
  );
});

const EquipmentRecordsTab = ({ setActiveModule, setActiveModuleWithTab, setShowVesselSettingsView }) => {
  const { currentVesselId, getCurrentVessel } = useVessel()
  const currentVessel = getCurrentVessel();
  // FT-459: also pull the dialog HOSTS (<ConfirmDialog/>, <AlertDialog/>) from
  // the SAME useCustomDialogs() instance. showConfirm/showAlert set state on
  // this instance, so its hosts must be mounted in this component's JSX or the
  // awaited confirm (e.g. the archive-restore confirmation) hangs and the
  // gated action never fires.
  const { showAlert, showConfirm, ConfirmDialog, AlertDialog } = useCustomDialogs();
  const { showDeleteDialog, logChange, canDelete, DeleteModal } = useAuditTrail('equipment');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const {
    // Primary API - arrays and counts
    requiredItems,
    reviewItems,
    addedItems,
    counts,
    // Core sync function
    syncFromRequirements,
    // Legacy compatibility
    items,
    // syncRequiredOnce removed - use syncFromRequirements instead (Phase 4 Fix 1)
    addItem,
    updateItem,
    archiveItem,
    restoreItem,      // FT-254
    removeItem,       // FT-254 (hard delete path)
    archivedItems,    // FT-254
    archivedCount,    // FT-254
    getFilteredItems,
    getAllItems,
    syncing,
    isFeatureEnabled,
    // Debug state
    debugState,
    dumpAllItems,
    runGlobalGuidanceAudit
  } = useEquipmentRegister();
  
  // Force refresh when equipment is updated
  const [refreshKey, setRefreshKey] = useState(0);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [itemFilter, setItemFilter] = useState('all'); // all, required, added (for FilterTabs)
  const [sourceFilter, setSourceFilter] = useState('all'); // keeping for compatibility
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showHistoryView, setShowHistoryView] = useState(false);
  const [hasUserSelections, setHasUserSelections] = useState(false);
  const [checkingSelections, setCheckingSelections] = useState(true);

  // Safe logging
  if (typeof devLog === 'function') {
    devLog('[EquipTab] Counts:', counts);
  }
  const [showAddForm, setShowAddForm] = useState(false);
  const [importBannerDismissed, setImportBannerDismissed] = useState(false);
  const [availableCount, setAvailableCount] = useState(0);
  const [syncedRequired, setSyncedRequired] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [showDetailView, setShowDetailView] = useState(false);

  // Load maintenance jobs and history for smart status calculation
  const [maintenanceJobs, setMaintenanceJobs] = useState([]);
  const [maintenanceHistory, setMaintenanceHistory] = useState([]);

  // BUG-005 related: Enhanced equipment with computed service status from job completions
  const [equipmentServiceStatus, setEquipmentServiceStatus] = useState({});

  // Loading states for async operations
  const [isSavingEquipment, setIsSavingEquipment] = useState(false);
  const [isUpdatingEquipment, setIsUpdatingEquipment] = useState(false);
  const [isDeletingEquipment, setIsDeletingEquipment] = useState(false);

  // BUG-005 related: Merge equipment service status into items for display
  // This enriches items from useEquipmentRegister with computed _lastServiceDate, etc.
  // FT-254: when the user selects the 'Archived' filter, source from archivedItems
  // (which the hook excludes from `items`) so archived equipment becomes visible.
  const enrichedItems = useMemo(() => {
    const source = itemFilter === 'archived' ? (archivedItems || []) : (items || []);
    if (source.length === 0) return source;
    if (Object.keys(equipmentServiceStatus).length === 0) return source;

    return source.map(item => {
      const status = equipmentServiceStatus[item.id];
      if (status) {
        return {
          ...item,
          _lastServiceDate: status._lastServiceDate,
          _lastServiceBy: status._lastServiceBy,
          _serviceCount: status._serviceCount,
          _hasBeenServiced: status._hasBeenServiced,
          _nextServiceDue: status._nextServiceDue
        };
      }
      return item;
    });
  }, [items, archivedItems, itemFilter, equipmentServiceStatus]);

  // Phase 3C Fix 5: Cached equipment type priority for sorting
  const getEquipmentTypePriority = (label) => {
    if (!label) return 999;

    // Check cache first
    if (priorityCache.has(label)) {
      return priorityCache.get(label);
    }

    const name = label.toLowerCase();
    let priority = 999;

    // Portable Extinguishers
    if (name.includes('portable extinguisher')) {
      if (name.includes('bridge')) priority = 100;
      else if (name.includes('engine')) priority = 101;
      else if (name.includes('galley')) priority = 102;
      else if (name.includes('accommodation')) priority = 103;
      else priority = 110;
    }
    // Fixed Fire Systems
    else if (name.includes('fixed fire system')) {
      if (name.includes('engine')) priority = 200;
      else if (name.includes('galley')) priority = 201;
      else if (name.includes('accommodation')) priority = 202;
      else priority = 210;
    }
    // Fire Equipment
    else if (name.includes('fire blanket')) priority = 300;
    else if (name.includes('fire pump')) priority = 310;
    else if (name.includes('fire hose')) priority = 311;
    else if (name.includes('fire control')) priority = 320;
    // Fire Detectors
    else if (name.includes('fire detector')) {
      if (name.includes('machinery')) priority = 400;
      else if (name.includes('galley')) priority = 401;
      else if (name.includes('accommodation')) priority = 402;
      else if (name.includes('electrical')) priority = 403;
      else if (name.includes('lithium')) priority = 404;
      else priority = 410;
    }
    // Gas Detectors
    else if (name.includes('gas detector')) {
      if (name.includes('hydrocarbon')) priority = 450;
      else if (name.includes('carbon monoxide') || name.includes('co')) priority = 451;
      else priority = 460;
    }

    // Cache result
    priorityCache.set(label, priority);

    return priority;
  };

  // Phase 3C Fix 4: Combined single-pass useMemo for all calculations
  const {
    filteredEquipment,
    domainCounts,
    groupedEquipment
  } = useMemo(() => {
    // BUG-005: Use enrichedItems which has computed _lastServiceDate from job completions
    const allEquipment = enrichedItems || [];

    // Initialize accumulators for single-pass calculation
    const counts = {
      domain: {},
      status: { total: 0, required: 0, userAdded: 0, overdue: 0 }
    };
    const filtered = [];
    const grouped = {};

    // Pre-compute search term for efficiency
    const searchLower = searchTerm?.toLowerCase() || '';

    // SINGLE PASS through all equipment
    allEquipment.forEach(item => {
      // Count domain (for all items)
      const domain = item.domain || normalizeDomain(item.category) || 'other';
      counts.domain[domain] = (counts.domain[domain] || 0) + 1;

      // Count status (for all items)
      counts.status.total++;
      if (item.source === 'requirements' || item.sourceRequirementId) counts.status.required++;
      if (item.source === 'added') counts.status.userAdded++;
      if (item.status === 'overdue') counts.status.overdue++;

      // Apply source filter
      if (itemFilter !== 'all') {
        if (itemFilter === 'required' && !(item.source === 'requirements' || item.sourceRequirementId)) return;
        if (itemFilter === 'added' && item.source !== 'added') return;
      }

      // Apply category filter
      if (categoryFilter !== 'all' && item.category?.toLowerCase() !== categoryFilter.toLowerCase()) {
        return;
      }

      // Apply search filter
      if (searchLower) {
        const matchesSearch =
          item.label?.toLowerCase().includes(searchLower) ||
          item.name?.toLowerCase().includes(searchLower) ||
          item.type?.toLowerCase().includes(searchLower) ||
          item.serialNumber?.toLowerCase().includes(searchLower) ||
          item.serialNo?.toLowerCase().includes(searchLower) ||
          item.location?.toLowerCase().includes(searchLower) ||
          item.notes?.toLowerCase().includes(searchLower);

        if (!matchesSearch) return;
      }

      // Add to filtered
      filtered.push(item);

      // Group by domain
      const groupDomain = normalizeDomain(item.category) || item.domain || 'other';
      if (!grouped[groupDomain]) grouped[groupDomain] = [];
      grouped[groupDomain].push(item);
    });

    // Sort filtered once (uses cached priority function)
    filtered.sort((a, b) => {
      const priorityA = getEquipmentTypePriority(a.label);
      const priorityB = getEquipmentTypePriority(b.label);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      const labelA = (a.label || '').toLowerCase();
      const labelB = (b.label || '').toLowerCase();
      return labelA.localeCompare(labelB);
    });

    return {
      filteredEquipment: filtered,
      domainCounts: counts.domain,
      statusCounts: counts.status,
      groupedEquipment: grouped
    };
  }, [enrichedItems, itemFilter, searchTerm, categoryFilter]);


  // Global guidance audit function attachment
  useEffect(() => {
    if (typeof window !== "undefined" && runGlobalGuidanceAudit) {
      window.runGlobalGuidanceAudit = runGlobalGuidanceAudit;
    }
    return () => {
      if (typeof window !== "undefined") {
        delete window.runGlobalGuidanceAudit;
      }
    };
  }, [runGlobalGuidanceAudit]);

  // Check for user equipment selections
  useEffect(() => {
    const checkUserSelections = () => {
      if (!currentVesselId) {
        setHasUserSelections(false);
        setCheckingSelections(false);
        return;
      }
      
      setCheckingSelections(true);
      const userSelections = loadUserEquipmentSelections(currentVesselId);
      setHasUserSelections(!!userSelections);
      setCheckingSelections(false);

    };
    
    checkUserSelections();
  }, [currentVesselId, items.length]);

  // FT-48: Read maintenance jobs from storage (not config) - Manual Maintenance Architecture
  useEffect(() => {
    if (currentVesselId) {
      // Load maintenance jobs from localStorage (same key as PlannedMaintenanceTab)
      const jobsKey = toOrgScopedKey(`maintenance_jobs_${currentVesselId}`);
      const savedJobs = JSON.parse(localStorage.getItem(jobsKey) || '[]');
      setMaintenanceJobs(savedJobs);

      // Load maintenance history
      const historyKey = toOrgScopedKey(`maintenance_history_${currentVesselId}`);
      const history = JSON.parse(localStorage.getItem(historyKey) || '[]');
      setMaintenanceHistory(history);
    }
  }, [currentVesselId]); // Only depend on ID, not the whole vessel object

  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Listen for equipment updates to force refresh
  useEffect(() => {
    const handleStorageUpdate = (event) => {
      if (event.detail?.module === 'equipment_register') {

        setRefreshKey(prev => prev + 1);
      }
    };
    
    window.addEventListener('sr:storage:set', handleStorageUpdate);
    return () => window.removeEventListener('sr:storage:set', handleStorageUpdate);
  }, []);

  // PERFORMANCE MONITORING - Equipment Register Optimization
  useEffect(() => {
    const startTime = performance.now();

    return () => {
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (duration > 500) {

      }
    };
  });

  // BUG-005 FIX: Load maintenance jobs and calculate service status from job.completions[]
  // This is the "permanent truth" - same source that maintenance cards use
  // PERF: Using static import + startTransition for better batching
  useEffect(() => {
    if (!currentVesselId || !items || items.length === 0) {
      return;
    }

    // Load maintenance jobs from localStorage (same as PlannedMaintenanceTab does)
    const jobsKey = toOrgScopedKey(`maintenance_jobs_${currentVesselId}`);
    const savedJobs = JSON.parse(localStorage.getItem(jobsKey) || '[]');

    if (savedJobs.length === 0) {

      return;
    }

    // Use startTransition to mark as non-urgent update (deprioritizes re-render cascade)
    startTransition(() => {
      const equipmentWithStatus = calculateEquipmentServiceStatus(items, savedJobs);

      // Create lookup map for enrichedItems useMemo
      const statusMap = {};
      equipmentWithStatus.forEach(eq => {
        if (eq._lastServiceDate) {  // Only include if there's actual service data
          statusMap[eq.id] = {
            _lastServiceDate: eq._lastServiceDate,
            _lastServiceBy: eq._lastServiceBy,
            _serviceCount: eq._serviceCount,
            _hasBeenServiced: eq._hasBeenServiced,
            _nextServiceDue: eq._nextServiceDue
          };
        }
      });

      setEquipmentServiceStatus(statusMap);

    });

  }, [currentVesselId, items]);

  // Refresh equipment when vessel changes
  useEffect(() => {

    setRefreshKey(prev => prev + 1);
  }, [currentVesselId]);

  // Check for equipment detail restoration from defects navigation
  useEffect(() => {
    // Check for both legacy key and new return context key
    const equipmentToShow = sessionStorage.getItem('equipment:showDetail');
    const reopenEquipmentId = sessionStorage.getItem('reopenEquipmentDetail');
    const equipmentIdToOpen = equipmentToShow || reopenEquipmentId;

    if (equipmentIdToOpen && items?.length > 0) {
      devLog('[EquipmentRecordsTab] Restoring equipment detail view:', equipmentIdToOpen);

      // Find the equipment by ID
      const equipment = items.find(item => item.id === equipmentIdToOpen);
      if (equipment) {
        setSelectedEquipment(equipment);
        setShowDetailView(true);
        devLog('[EquipmentRecordsTab] Equipment found, opening detail view');
      } else {
        devLog('[EquipmentRecordsTab] Equipment not found for ID:', equipmentIdToOpen);
      }

      // Clear the sessionStorage flags (consumed)
      sessionStorage.removeItem('equipment:showDetail');
      sessionStorage.removeItem('reopenEquipmentDetail');
    }
  }, [items]); // Depend on items so it runs when equipment loads
  
  // Update selectedEquipment when items change (to get latest data including units)
  useEffect(() => {
    if (selectedEquipment && items) {
      const updatedEquipment = items.find(item => item.id === selectedEquipment.id);
      if (updatedEquipment) {

        setSelectedEquipment(updatedEquipment);
      }
    }
  }, [items, selectedEquipment?.id]); // Depend on selectedEquipment.id to avoid stale closures

  // Cache dry-run sync results to avoid repeated expensive calculations
  const [availableCountCache, setAvailableCountCache] = useState(null);
  const [cacheVesselId, setCacheVesselId] = useState(null);

  useEffect(() => {
    // Use cached result if vessel hasn't changed
    if (cacheVesselId === currentVesselId && availableCountCache !== null) {
      setAvailableCount(availableCountCache);

      return;
    }

    // Run dry-run sync only if cache miss
    const checkAvailable = async () => {



      if (!isFeatureEnabled()) {

        return;
      }
      
      if (!currentVessel) {

        return;
      }
      
      // This will get the count of applicable equipment (Phase 4 Fix 1: use syncFromRequirements)
      const result = await syncFromRequirements({ dryRun: true });

      // Phase 4 Fix 2.3: Mark dry-run state updates as low priority
      if (result && result.applicableCount) {
        startTransition(() => {
          setAvailableCount(result.applicableCount);
          setAvailableCountCache(result.applicableCount);
          setCacheVesselId(currentVesselId);
        });

      } else {
        // Fallback: if dry run doesn't work, estimate based on vessel type

        // Basic vessels need about 30-40 items, larger vessels 50-80
        const estimate = currentVessel?.category <= 2 ? 60 : 35;
        startTransition(() => {
          setAvailableCount(estimate);
          setAvailableCountCache(estimate);
          setCacheVesselId(currentVesselId);
        });
      }
    };

    checkAvailable();
  }, [currentVesselId, syncFromRequirements, cacheVesselId, availableCountCache]); // Re-check when vessel changes

  // FT-52: Removed auto-sync from requirements on first mount
  // Equipment is now added manually via the Add Equipment button

  // Check for success notification when returning from Settings
  useEffect(() => {
    const justAdded = sessionStorage.getItem('equipment:justAdded');
    const addedTab = sessionStorage.getItem('equipment:addedTab');

    if (justAdded === 'true') {
      // Clean up flags
      sessionStorage.removeItem('equipment:justAdded');
      sessionStorage.removeItem('equipment:addedTab');

      // Show success message
      showAlert('Equipment added successfully and linked to domain requirements');

      // Refresh equipment list
      setRefreshKey(prev => prev + 1);

      // Restore user's tab if provided
      if (addedTab && addedTab !== 'all') {
        setItemFilter(addedTab);
      }

    }
  }, []); // Run only once on mount

  // Open AddEquipmentView modal for custom equipment
  const handleAddEquipment = () => {
    setShowAddForm(true);
  };

  // Save equipment from AddEquipmentView form
  const handleSaveEquipment = async (equipmentData) => {
    setIsSavingEquipment(true);
    try {
      // Add to Equipment Register using the hook
      // addItem() automatically sets source: 'added' for user-added equipment
      const newItem = addItem(equipmentData);

      // FT-512: record the addition in the audit trail (Equipment > History /
      // ChangeLogViewer). Mirrors the delete (~735) and restore (~790) patterns:
      // same useAuditTrail('equipment') logChange instance, same awaited shape.
      // - action 'created' matches the existing blue "Created" badge in
      //   ChangeLogViewer.getActionBadge; 'create' falls through to grey.
      // - logChange stamps user/timestamp/vessel itself from usePermissions()/
      //   useVessel(), so we DO NOT pass a user field.
      // - guard on newItem: addItem returns null when the register isn't loaded;
      //   logging a null item would throw inside logChange (item.id deref).
      if (newItem) {
        await logChange({
          action: 'created',
          item: newItem,
          reason: 'User added equipment'
        });
      }

      // Show success message
      showAlert('Equipment added successfully');

      // CRITICAL FIX (BUG-001 Part 4): Wait for both debounce layers to complete
      // useEquipmentRegister has 100ms debounce, useStorageState has 50ms debounce
      // 200ms ensures both layers flush to localStorage before modal unmounts
      await new Promise(resolve => setTimeout(resolve, 200));

      // Close the form (now safe - data is persisted)
      setShowAddForm(false);

      // Dispatch event to refresh other components
      window.dispatchEvent(new CustomEvent('sr:equipment:updated', {
        detail: { vesselId: currentVesselId }
      }));
    } catch (error) {

      showAlert(`Could not save "${equipmentData.name || 'equipment'}". Check if name is unique and all required fields are filled.`);
    } finally {
      setIsSavingEquipment(false);
    }
  };

  // View equipment in full-screen detail view
  const handleViewEquipment = (equipment) => {
    setSelectedEquipment(equipment);
    setShowDetailView(true);
  };

  // Update equipment from detail view
  const handleUpdateEquipment = async (id, updates) => {
    setIsUpdatingEquipment(true);
    try {
      // Update the equipment item in the register using the hook
      const result = updateItem(id, updates);
      showAlert('Equipment updated successfully');
    } catch (error) {

      showAlert(`Failed to update equipment. ${error.message || 'Please check your connection and try again.'}`);
    } finally {
      setIsUpdatingEquipment(false);
    }
  };

  // Delete equipment
  const handleDeleteEquipment = async (id) => {
    const item = items.find(i => i.id === id);

    if (!item) {
      showAlert('Equipment not found');
      return;
    }

    // Prevent deletion of required equipment
    if (item.source === 'requirements') {
      showAlert('Cannot delete required equipment. This equipment is mandated by regulations for your vessel configuration.');
      return;
    }

    // Check permissions
    if (!canDelete) {
      showAlert('You do not have permission to delete equipment.');
      return;
    }

    // Show delete reason modal
    const reasonData = await showDeleteDialog(item, async (reason) => {
      setIsDeletingEquipment(true);
      try {
        await logChange({
          action: 'delete',
          item: item,
          reason: reason.reason,
          authorizedBy: reason.authorizedBy,
          deleteType: reason.deleteType
        });

        // FT-254 (2026-05-31): honour the deleteType from the reason modal.
        // Previously this always called archiveItem regardless of the user's
        // soft/hard choice — the 'Permanent delete' radio was decorative.
        // Runtime evidence: Red Alert cloud probe after a 'Permanent delete'
        // of TEST4 found the item still present with archived:true rather than
        // removed from storage.
        if (reason.deleteType === 'hard') {
          await removeItem(id);  // hard delete — gone from storage permanently
        } else {
          await archiveItem(id, reason.reason || 'User deleted');  // soft delete (default)
        }

        // Close detail view after deletion
        setShowDetailView(false);
        setSelectedEquipment(null);

        return true;
      } catch (error) {

        showAlert(`Failed to ${reason.deleteType === 'hard' ? 'delete' : 'archive'} equipment. ${error.message || 'Please try again.'}`);
        return false;
      } finally {
        setIsDeletingEquipment(false);
      }
    });

    if (reasonData) {
      showAlert(reasonData.deleteType === 'hard' ? 'Equipment permanently deleted' : 'Equipment archived successfully');
    }
  };

  // FT-254: Restore an archived equipment item back into the active register.
  // Symmetric to handleDeleteEquipment but no reason modal — restoring is benign.
  // The hook's restoreItem preserves archivedAt/archivedReason as audit trail.
  const handleRestoreEquipment = async (id) => {
    const item = (archivedItems || []).find(i => i.id === id);
    if (!item) {
      showAlert('Archived equipment not found');
      return;
    }
    const ok = await showConfirm(
      'Restore equipment?',
      `"${item.name || item.label || 'This item'}" will be restored to the active equipment register.`
    );
    if (!ok) return;
    try {
      restoreItem(id);
      await logChange({
        action: 'restore',
        item: item,
        reason: 'User restored from archive'
      });
      showAlert('Equipment restored successfully');
    } catch (error) {
      showAlert(`Failed to restore equipment. ${error.message || 'Please try again.'}`);
    }
  };

  // Clear all equipment (for testing)
  const handleClearAll = async () => {
    if (window.confirm('This will clear all equipment records. Continue?')) {
      localStorage.removeItem(`equipment_register_${currentVesselId}`);
      window.location.reload();
    }
  };

  // Import required equipment from requirements
  const handleImportRequired = async () => {

    try {
      const result = await syncFromRequirements();

      if (result.success) {
        const changeCount = result.created?.length || 0;
        const updateCount = result.updated?.length || 0;
        const archivedCount = result.archived?.length || 0;
        
        if (changeCount > 0 || updateCount > 0 || archivedCount > 0) {
          let message = `Sync complete: `;
          if (changeCount > 0) message += `${changeCount} added, `;
          if (updateCount > 0) message += `${updateCount} updated, `;
          if (archivedCount > 0) message += `${archivedCount} archived`;
          showAlert(message.replace(/, $/, ''));
          setImportBannerDismissed(true);
        } else {
          showAlert('All equipment up to date');
          setImportBannerDismissed(true);
        }
      } else {

        showAlert('Sync failed: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {

      showAlert('Import failed: ' + error.message);
    }
  };

  // Navigate to Equipment Selection Tab
  const handleConfigureEquipment = () => {
    // Emit event to switch to Equipment Selection Tab
    window.dispatchEvent(new CustomEvent('navigation:tab', { 
      detail: { tab: 'equipment-selection' }
    }));
  };




  // Get canonical equipment from the register - use hook data directly
  const allEquipment = getAllItems() || [];
  const canonicalFFE = allEquipment.filter(
    item => item && item.category === 'FFE' && !item.archived
  );

  // Use hook's authoritative counts (Domain Spine v2 source of truth)
  const requiredCount = counts.required;
  const addedCount = counts.added;
  const totalCount = counts.total;

  // DEBUG: Add global functions for debugging equipment categories
  window.debugEquipmentCategories = () => {

    filteredEquipment.slice(0, 10).forEach((item, index) => {






    });
  };
  

  // DEBUG: Log equipment data to understand what's available

  // Use memoized groupedEquipment with FFE detector override
  const finalGroupedEquipment = useMemo(() => {
    const grouped = { ...groupedEquipment };
    
    // Apply FFE detector override to memoized groups
    Object.keys(grouped).forEach(domain => {
      grouped[domain] = grouped[domain].map(item => {
        // Force FFE detectors (including hydrocarbon) into the FFE domain
        if (
          item.category === 'FFE' ||
          item.sourceRequirementId?.startsWith('ffe_detector') ||
          item.sourceRequirementId === 'ffe_detector_gas_galley'
        ) {
          // Move to FFE domain if not already there
          if (domain !== 'ffe') {
            if (!grouped['ffe']) grouped['ffe'] = [];
            grouped['ffe'].push(item);
            return null; // Mark for removal from current domain
          }
        }
        return item;
      }).filter(Boolean);
    });
    
    // Clean up empty domains
    Object.keys(grouped).forEach(domain => {
      if (grouped[domain].length === 0) {
        delete grouped[domain];
      }
    });
    
    return grouped;
  }, [groupedEquipment]);

  // REMOVED FFE-only override - now showing all equipment domains as intended
  // Previously this was forcing only FFE items to display, hiding LSA, Navigation, GMDSS, etc.
  // The grouping logic above (lines 660-677) already correctly organizes equipment by domain

  // Get FFE items for debug function - explicit filter
  const ffeItems = allEquipment.filter(eq =>
    (eq.category === 'FFE' || eq.sourceRequirementId?.startsWith('ffe_detector')) &&
    !eq.archived
  );

  // FFE UI debug – expose what the Equipment Register is actually rendering
  if (typeof window !== 'undefined') {
    window.__debugEquipmentFFE = () => {

      return ffeItems;
    };
  }

  // Get category for equipment type
  function getCategoryForType(type) {
    const categories = {
      'fire_extinguisher': 'Fire Fighting',
      'fire_blanket': 'Fire Fighting',
      'fire_hose': 'Fire Fighting',
      'lifejacket': 'Life Saving',
      'liferaft': 'Life Saving',
      'lifebuoy': 'Life Saving',
      'epirb': 'Emergency',
      'sart': 'Emergency',
      'first_aid_kit': 'Medical',
      'medical_kit': 'Medical',
      'flares': 'Distress Signals',
      'smoke_signal': 'Distress Signals'
    };
    return categories[type] || 'Other';
  }

  // Get icon for category
  function getCategoryIcon(category) {
    const icons = {
      'Fire Fighting': <Flame className="h-5 w-5" />,
      'Life Saving': <LifeBuoy className="h-5 w-5" />,
      'Emergency': <Radio className="h-5 w-5" />,
      'Medical': <Heart className="h-5 w-5" />,
      'Distress Signals': <Radio className="h-5 w-5" />,
      'Other': <Package className="h-5 w-5" />
    };
    return icons[category] || <Package className="h-5 w-5" />;
  }

  // Get category stats
  const categoryStats = Object.keys(finalGroupedEquipment).map(category => ({
    name: category,
    count: finalGroupedEquipment[category].length,
    icon: getCategoryIcon(category)
  }));

  // Disable families view - use standard equipment list
  const useFamiliesView = false; // DomainPackBridge.getFeatureFlag('FEATURE_DOMAINPACKS_V3.equipmentFamilies');

  // FT-358: register middle-slot bottom-bar actions per brief §6
  // ([+ Add] / [History]) — Export dropped from list, still available on
  // individual equipment history views.
  const isSubView = showAddForm || showDetailView || showHistoryView;
  useSetBottomBarActions(
    isSubView
      ? []
      : [
          {
            key: 'equip-add',
            icon: PlusCircle,
            label: 'Add',
            primary: true,
            onClick: handleAddEquipment,
          },
          {
            key: 'equip-history',
            icon: Clock,
            label: 'History',
            onClick: () => setShowHistoryView(true),
          },
        ],
  );

  // If families view is enabled, show that instead
  if (useFamiliesView) {
    // Get vessel config from the same source as Settings panel
    const rawConfig = vesselConfigStore.getConfig();

    // Build config matching EXACTLY what Settings panel uses
    const formattedConfig = {
      // Basic info - Settings uses these directly
      vesselName: rawConfig.basic?.name,
      vesselType: rawConfig.basic?.type,
      category: rawConfig.basic?.category,
      lengthOverall: rawConfig.basic?.lengthOverall,
      grossTonnage: rawConfig.basic?.grossTonnage,
      engineType: rawConfig.machinery?.engineType || 'diesel',
      enginePower: rawConfig.machinery?.enginePowerKW,
      enginePosition: rawConfig.machinery?.engineLocation || 'below_deck',
      operatingStatus: rawConfig.basic?.operatingStatus || 'seagoing',
      
      // Capacity
      passengerCapacity: rawConfig.capacity?.passengerCapacity,
      crewCount: rawConfig.capacity?.crewSize,
      maxPersons: rawConfig.capacity?.pob,
      
      // These are CRITICAL - Settings uses them flat, not nested!
      hasAccommodation: rawConfig.accommodation?.hasAccommodation || false,
      hasGalley: rawConfig.accommodation?.hasCooking || false,
      hasSleeping: rawConfig.accommodation?.hasSleeping || false,
      hasEnclosedSpaces: rawConfig.environmental?.hasSubstantialEnclosure || false,
      hasCabin: rawConfig.accommodation?.hasAccommodation || false,
      hasWheelhouse: rawConfig.environmental?.hasSubstantialEnclosure || false,
      
      // Machinery - Default to true for vessels with engines
      // Check if explicitly set, otherwise default based on engine type
      hasMachinerySpace: rawConfig.machinery?.hasMachinerySpace === true || 
                        (rawConfig.machinery?.hasMachinerySpace !== false && 
                         rawConfig.machinery?.engineType && 
                         rawConfig.machinery?.engineType !== 'none'),
      
      // Gas/fuel appliances
      hasGasAppliances: rawConfig.accommodation?.hasGasAppliance || false,
      hasLPGSystem: rawConfig.accommodation?.hasGasAppliance || false,
      cookingType: rawConfig.accommodation?.hasOpenFlame ? 'gas' : 'electric',
      
      // Fire-related
      hasFireRiskArea: rawConfig.environmental?.hasFireRiskArea || false,
      hasConcentratedElectrical: rawConfig.environmental?.hasConcentratedElectrical || false,
      hasExhaustLeakRisk: rawConfig.machinery?.hasExhaustLeakRisk || false,
      hasGasLeakRisk: rawConfig.environmental?.hasGasLeakRisk || false,
      hasLithiumBatteries: rawConfig.accommodation?.hasLithiumBattery || false,
      
      // Escape routes
      hasEscapeRoute: rawConfig.environmental?.hasEscapeRoute || 
                     rawConfig.accommodation?.hasAccommodation ||
                     (rawConfig.machinery?.hasMachinerySpace && rawConfig.machinery?.engineType !== 'outboard'),
      
      // Operations
      operations: rawConfig.operations?.operations || []
    };
    
    return <EquipmentFamiliesView 
      onSelectFamily={(family) => console.log('Selected family:', family)} 
      vesselConfig={formattedConfig}
    />;
  }

  // AddEquipmentView - Add custom equipment (source: 'added')
  if (showAddForm) {
    return (
      <AddEquipmentView
        onBack={() => setShowAddForm(false)}
        onSave={handleSaveEquipment}
        isSaving={isSavingEquipment}
      />
    );
  }
  
  // If showing detail view, render full-screen component
  if (showDetailView && selectedEquipment) {
    return (
      <EquipmentDetailView
        equipment={selectedEquipment}
        onBack={() => {
          setShowDetailView(false);
          setSelectedEquipment(null);
        }}
        onUpdate={handleUpdateEquipment}
        onDelete={handleDeleteEquipment}
        onAfterDelete={() => {
          setShowDetailView(false);
          setSelectedEquipment(null);
        }}
        onAddItem={addItem}
        setActiveModule={setActiveModule}
        setActiveModuleWithTab={setActiveModuleWithTab}
        isUpdating={isUpdatingEquipment}
        isDeleting={isDeletingEquipment}
      />
    );
  }

  // Debug canonical FFE before render

  window.__debugEquipmentFFE && window.__debugEquipmentFFE();

  // Show loading skeleton while data initializes
  if (syncing || checkingSelections) {
    return (
      <div className="p-6 space-y-4">
        {/* Header skeleton */}
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-10 bg-gray-200 rounded w-full mb-6"></div>
        </div>

        {/* Stats cards skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 rounded-lg h-24"></div>
            </div>
          ))}
        </div>

        {/* Equipment list skeleton */}
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="animate-pulse">
              <div className="bg-gray-200 rounded-lg h-20"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Equipment Change History - Full-screen view
  if (showHistoryView) {
    return <ChangeLogViewer onBack={() => setShowHistoryView(false)} />;
  }

  // FT-52: Updated empty state to point to Add Equipment form instead of Settings Step 8
  if (!items || items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-6">
        {/* Compliance Warning */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 max-w-md">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-amber-900">Compliance Notice</h4>
              <p className="text-sm text-amber-800 mt-1">
                Add your vessel's safety equipment to maintain compliance records.
                Use the 'Add Equipment' button to get started.
              </p>
            </div>
          </div>
        </div>

        {/* Empty State */}
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <Package className="h-8 w-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Equipment Added
        </h3>
        <p className="text-gray-500 text-center mb-6 max-w-md">
          Add equipment items to build your vessel's equipment register and track maintenance.
        </p>
        <button
          onClick={handleAddEquipment}
          className="inline-flex items-center px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Equipment
        </button>
      </div>
    );
  }

  return (
    <>
      {/* Full-width layout for Equipment Register */}
      <div className="pb-20 lg:pb-0">
        <div className="rounded-2xl border border-gray-200 shadow-sm bg-white overflow-hidden">
            {/* Main Header with Search */}
            <div className="bg-slate-700 px-5 py-3 rounded-t-2xl">
              <div className="flex items-center justify-between gap-6">
                {/* Title - Responsive size */}
                <h3 className="text-base font-semibold text-white whitespace-nowrap flex-shrink-0">
                  Equipment<span className="hidden sm:inline"> Register</span>
                </h3>

                {/* Search Input */}
                <div className="relative flex-1 max-w-2xl">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search equipment..."
                    aria-label="Search equipment by name, location, or serial number"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 bg-white text-slate-800 text-sm border border-white/20 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 transition-all duration-200"
                  />
                </div>

                {/* Add Equipment Button - Desktop only (mobile uses bottom bar) */}
                <button
                  onClick={handleAddEquipment}
                  className="hidden lg:flex items-center gap-2 px-4 py-2 bg-cyan-500 hover:bg-cyan-400 text-white text-sm font-medium rounded-md transition-colors whitespace-nowrap"
                >
                  <Plus className="h-4 w-4" />
                  Add Equipment
                </button>

                {/* FT-262: History button — desktop only (mobile uses bottom bar at line 1612).
                    Opens the equipment change log (audit trail of add/edit/delete/restore actions)
                    via ChangeLogViewer. */}
                <button
                  onClick={() => setShowHistoryView(true)}
                  className="hidden lg:flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-md transition-colors whitespace-nowrap"
                  title="View equipment change history"
                >
                  <History className="h-4 w-4" />
                  History
                </button>
              </div>
            </div>

            {/* Content Section */}
            <div className="py-5 relative">
              {/* Filter Row */}
              <div className="flex items-center gap-3 px-6 mb-6">
                {/* Category Filter Dropdown */}
                <Dropdown
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                  width="flex-1 sm:flex-none sm:w-44"
                  options={[
                    { value: 'all', label: 'All Categories' },
                    { value: 'lsa', label: 'LSA' },
                    { value: 'ffe', label: 'FFE' },
                    { value: 'machinery', label: 'Machinery' },
                    { value: 'navigation', label: 'Navigation' },
                    { value: 'medical', label: 'Medical' },
                    { value: 'gmdss', label: 'GMDSS' },
                    { value: 'electrical', label: 'Electrical' },
                    { value: 'environmental', label: 'Environmental' },
                    { value: 'operations', label: 'Operations' },
                    { value: 'other', label: 'Other' },
                  ]}
                />

                {/* Source Filter Dropdown */}
                <Dropdown
                  value={itemFilter}
                  onChange={setItemFilter}
                  width="flex-1 sm:flex-none sm:w-40"
                  options={[
                    { value: 'all', label: `All (${totalCount})` },
                    { value: 'required', label: `Required (${requiredCount})` },
                    { value: 'added', label: `Added (${addedCount})` },
                    // FT-254: surface archived items so users can view + restore them
                    { value: 'archived', label: `Archived (${archivedCount || 0})` },
                  ]}
                />
              </div>

              {/* Export Dropdown */}
              {showExportDropdown && (
                <>
                  {/* Backdrop to close dropdown */}
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowExportDropdown(false)}
                  />

                  {/* Dropdown menu */}
                  <div className="absolute right-6 top-20 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
                    <div className="py-1" role="menu">
                      <button
                        onClick={() => {
                          downloadEquipmentCSV(filteredEquipment, currentVessel);
                          setShowExportDropdown(false);
                          showAlert('Equipment register exported as CSV');
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center transition-colors duration-200"
                        role="menuitem"
                      >
                        <Download className="h-4 w-4 mr-2 text-gray-400" />
                        Export as CSV
                        <span className="ml-auto text-xs text-gray-500">Spreadsheet</span>
                      </button>

                      <button
                        onClick={() => {
                          downloadEquipmentPDF(filteredEquipment, currentVessel);
                          setShowExportDropdown(false);
                          showAlert('Equipment register exported as PDF');
                        }}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center transition-colors duration-200"
                        role="menuitem"
                      >
                        <Download className="h-4 w-4 mr-2 text-gray-400" />
                        Export as PDF
                        <span className="ml-auto text-xs text-gray-500">Document</span>
                      </button>
                    </div>
                  </div>
                </>
              )}

            {/* Review Items Banner */}
            {(counts?.review || 0) > 0 && sourceFilter !== 'required' && sourceFilter !== 'added' && (
              <div className="px-6 py-4 bg-yellow-50 border-b border-yellow-200">
                <div className="flex items-start">
                  <Shield className="h-5 w-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-yellow-900">
                      {counts?.review || 0} equipment items need review
                    </h4>
                    <p className="mt-1 text-sm text-yellow-700">
                      Complete vessel configuration to determine if these items are required.
                      <button 
                        onClick={() => setSourceFilter('review')}
                        className="ml-2 text-yellow-800 underline hover:text-yellow-900"
                      >
                        View items
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Equipment Display - Organized by Domain Sections (matching Step 7) */}
            <div className="pb-20 md:pb-0">
              {Object.keys(finalGroupedEquipment).length === 0 ? (
                <div>
                  {/* Check if we have user selections configured */}
                  {!hasUserSelections && !checkingSelections && !searchTerm && categoryFilter === 'all' ? (
                    /* No user selections - show Equipment Register empty state */
                    <EquipmentRegisterEmptyState onConfigureEquipment={handleConfigureEquipment} />
                  ) : (
                    /* Has user selections but no equipment showing (filters/search/auto-populated) */
                    <div className="px-6 py-12 text-center">
                      {/* Import Required Equipment Banner - show only for auto-populated content */}
                      {!searchTerm && categoryFilter === 'all' && !syncedRequired && 
                       isFeatureEnabled() && !importBannerDismissed && availableCount > 0 && (
                        <div className="mb-6 p-4 bg-cyan-50 border border-cyan-200 rounded-lg">
                          <div className="flex items-center justify-center mb-3">
                            <Shield className="h-5 w-5 text-cyan-600 mr-2" />
                            <h4 className="text-sm font-medium text-cyan-900">
                              This vessel has equipment available. Import now?
                            </h4>
                          </div>
                          <div className="flex justify-center gap-3">
                            <button
                              onClick={handleImportRequired}
                              disabled={syncing}
                              className="px-4 py-2 bg-cyan-600 text-white rounded-md hover:bg-cyan-700
                                       disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors duration-200"
                            >
                              {syncing ? 'Importing...' : `Import Equipment`}
                            </button>
                            <button
                              onClick={() => setImportBannerDismissed(true)}
                              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium transition-colors duration-200"
                            >
                              Dismiss
                            </button>
                          </div>
                        </div>
                      )}
                      
                      <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-sm font-medium text-gray-900 mb-2">No equipment records</h3>
                      <p className="text-sm text-gray-500 mb-4">
                        {searchTerm || categoryFilter !== 'all' 
                          ? 'No equipment found matching your filters.'
                          : 'Get started by adding your vessel\'s safety equipment.'}
                      </p>
                      {!searchTerm && categoryFilter === 'all' && (
                        <button
                          onClick={handleAddEquipment}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 transition-colors duration-200"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add First Equipment
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Equipment organized in domain sections - Card layout */
                <div className="space-y-6">
                  {Object.keys(finalGroupedEquipment)
                    .sort((a, b) => {
                      const order = ['lsa', 'ffe', 'navigation', 'gmdss', 'medical', 'machinery', 'electrical', 'environmental', 'operations', 'crew', 'misc'];
                      const idxA = order.indexOf(a);
                      const idxB = order.indexOf(b);
                      // Unknown (custom) domains sort after known ones but before misc
                      const rankA = idxA === -1 ? order.length - 1 : idxA;
                      const rankB = idxB === -1 ? order.length - 1 : idxB;
                      return rankA - rankB;
                    })
                    .map(domain => {
                      let domainEquipment = finalGroupedEquipment[domain];
                      // Known domains use short labels; custom domains capitalise the slug
                      const domainLabel = DOMAIN_LABELS[domain] ||
                        domain.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                      
                      // Note: Lifejacket filtering and deduplication now handled at data source level in useEquipmentRegister
                      
                      // Debug FFE items specifically  
                      if (domain === 'ffe') {

                        // Check specifically for gas detector
                        const gasDetector = domainEquipment.find(item => 
                          item.sourceRequirementId === 'ffe_detector_gas_galley' ||
                          (item.name || item.label || '').includes('Hydrocarbon Gas Detector')
                        );

                      }

                      return (
                        <div key={domain} className="mb-6 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                          {/* Domain Header - Refined file tab with curved edge */}
                          <div
                            className="bg-slate-700 px-5 py-3 flex items-center justify-between"
                            style={{
                              borderTopLeftRadius: '8px',
                              borderTopRightRadius: '12px'
                            }}
                          >
                            {/* Left: Icon + Title */}
                            <div className="flex items-center gap-3">
                              {/* Domain Icon - White for cohesive appearance */}
                              {domain === 'ffe' && <Flame className="h-6 w-6 text-white" />}
                              {domain === 'lsa' && <LifeBuoy className="h-6 w-6 text-white" />}
                              {domain === 'navigation' && <MapPin className="h-6 w-6 text-white" />}
                              {domain === 'gmdss' && <Radio className="h-6 w-6 text-white" />}
                              {domain === 'machinery' && <Cog className="h-6 w-6 text-white" />}
                              {domain === 'medical' && <Heart className="h-6 w-6 text-white" />}
                              {domain === 'electrical' && <Zap className="h-6 w-6 text-white" />}
                              {domain === 'environmental' && <Droplets className="h-6 w-6 text-white" />}
                              {domain === 'operations' && <Anchor className="h-6 w-6 text-white" />}
                              {!['ffe', 'lsa', 'navigation', 'gmdss', 'machinery', 'medical', 'electrical', 'environmental', 'operations'].includes(domain) && <Package className="h-6 w-6 text-white" />}

                              <h4 className="text-base font-semibold text-white m-0">{domainLabel}</h4>
                            </div>

                            {/* Right: Item count */}
                            <span className="text-sm font-medium text-slate-200">
                              {domainEquipment.length} item{domainEquipment.length !== 1 ? 's' : ''}
                            </span>
                          </div>

                          {/* Domain Equipment - Responsive Display */}
                          {isMobile ? (
                            /* Mobile Cards View */
                            <div className="px-4 pt-4 pb-4 space-y-3">
                              {domainEquipment.map(item => (
                                <EquipmentCard
                                  key={item.id}
                                  item={item}
                                  onView={() => handleViewEquipment(item)}
                                  maintenanceJobs={maintenanceJobs}
                                  maintenanceHistory={maintenanceHistory}
                                />
                              ))}
                            </div>
                          ) : (
                            /* Desktop Table View */
                            <div className="px-6 py-4">
                              <table className="min-w-full table-fixed" style={{tableLayout: 'fixed', width: '100%'}}>
                                <thead className="bg-gray-50">
                                  <tr className="border-b border-gray-200">
                                    <th className="w-1/3 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                      Equipment
                                    </th>
                                    <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                      Location
                                    </th>
                                    <th className="w-1/6 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                      Serial no.
                                    </th>
                                    <th className="w-1/4 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                      Service Status
                                    </th>
                                    <th className="w-1/12 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                                      Actions
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {domainEquipment.map(item => {
                                    // Calculate status using smart linking system
                                    const { status: serviceStatus, worstJob } = calculateEquipmentStatus(
                                      item,
                                      maintenanceJobs,
                                      maintenanceHistory
                                    );

                                    // FT-142 FIX: Read service dates from multiple sources
                                    // Priority: direct field > computed field > serviceHistory[]
                                    const sortedHistory = item.serviceHistory?.length > 0
                                      ? [...item.serviceHistory].sort((a, b) => new Date(b.date) - new Date(a.date))
                                      : [];
                                    const latestService = sortedHistory[0];
                                    const lastServiceDate = item.lastServiced || item._lastServiceDate || latestService?.date;
                                    const lastService = lastServiceDate ? new Date(lastServiceDate) : null;
                                    const nextDue = item.nextServiceDue || item._nextServiceDue || latestService?.nextDue;

                                    return (
                                      <tr 
                                        key={item.id}
                                        className="hover:bg-gray-50 cursor-pointer group transition-colors duration-200"
                                        onClick={(e) => {
                                          if (e.target.closest('button') || e.target.closest('[role="menuitem"]')) return;
                                          handleViewEquipment(item);
                                        }}
                                      >
                                        <td className="w-1/3 px-6 py-4">
                                          <div className="flex items-center min-w-0">
                                            {domain === 'ffe' && <Flame className="h-6 w-6 text-cyan-600 mr-3 flex-shrink-0" />}
                                            {domain === 'lsa' && <LifeBuoy className="h-6 w-6 text-cyan-600 mr-3 flex-shrink-0" />}
                                            {domain === 'navigation' && <MapPin className="h-6 w-6 text-cyan-600 mr-3 flex-shrink-0" />}
                                            {domain === 'gmdss' && <Radio className="h-6 w-6 text-cyan-600 mr-3 flex-shrink-0" />}
                                            {domain === 'machinery' && <Cog className="h-6 w-6 text-cyan-600 mr-3 flex-shrink-0" />}
                                            {domain === 'medical' && <Heart className="h-6 w-6 text-cyan-600 mr-3 flex-shrink-0" />}
                                            {domain === 'electrical' && <Zap className="h-6 w-6 text-cyan-600 mr-3 flex-shrink-0" />}
                                            {domain === 'environmental' && <Droplets className="h-6 w-6 text-cyan-600 mr-3 flex-shrink-0" />}
                                            {domain === 'operations' && <Anchor className="h-6 w-6 text-cyan-600 mr-3 flex-shrink-0" />}
                                            {!['ffe', 'lsa', 'navigation', 'gmdss', 'machinery', 'medical', 'electrical', 'environmental', 'operations'].includes(domain) && <Package className="h-6 w-6 text-cyan-600 mr-3 flex-shrink-0" />}
                                            <div className="min-w-0 flex-1 overflow-hidden">
                                              <div className="text-sm font-medium text-gray-900 truncate" title={getEquipmentDisplayName(item)}>
                                                {getEquipmentDisplayName(item)}
                                                {item.source === 'added' && item.instanceOf && (
                                                  <span className="ml-2 text-xs text-gray-500">(Unit {item.unitNumber || '2'})</span>
                                                )}
                                              </div>
                                              <div className="text-xs text-gray-500 mt-0.5 truncate">
                                                {(item.source === 'requirements' || item.sourceRequirementId) ? 'Required' : 'Added'}
                                              </div>
                                            </div>
                                          </div>
                                        </td>
                                        <td className="w-1/6 px-6 py-4">
                                          <div className="text-sm text-gray-900">
                                            {item.location ? item.location.charAt(0).toUpperCase() + item.location.slice(1).replace(/_/g, ' ') : 'General'}
                                          </div>
                                        </td>
                                        <td className="w-1/6 px-6 py-4 text-sm text-gray-500 overflow-hidden">
                                          <div className="truncate" title={item.serialNo || item.serialNumber || '—'}>
                                            {item.serialNo || item.serialNumber || '—'}
                                          </div>
                                        </td>
                                        <td className="w-1/4 px-6 py-4">
                                          <div className="space-y-1">
                                            {nextDue ? (
                                              <>
                                                <NextCheckBadge nextCheckDue={nextDue} />
                                                {lastService && (
                                                  <div className="text-xs text-gray-500">
                                                    Last: {lastService.toLocaleDateString('en-GB', {
                                                      day: 'numeric',
                                                      month: 'short',
                                                      year: 'numeric'
                                                    })}
                                                  </div>
                                                )}
                                              </>
                                            ) : lastService ? (
                                              <div className="text-sm text-gray-600">
                                                Last: {lastService.toLocaleDateString('en-GB', {
                                                  day: 'numeric',
                                                  month: 'short',
                                                  year: 'numeric'
                                                })}
                                              </div>
                                            ) : (
                                              <span className="text-sm text-gray-500">Never serviced</span>
                                            )}
                                          </div>
                                        </td>
                                        <td className="w-1/12 px-6 py-4 text-center">
                                          {/* FT-254: when viewing archived items, show Restore. Otherwise show Delete (which archives). */}
                                          {item.archived ? (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleRestoreEquipment(item.id);
                                              }}
                                              className="inline-flex items-center justify-center p-1.5 rounded-md text-green-600 hover:bg-green-50 transition-colors"
                                              title="Restore equipment"
                                            >
                                              <RotateCcw className="h-4 w-4" />
                                            </button>
                                          ) : (item.source !== 'requirements' && canDelete && (
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeleteEquipment(item.id);
                                              }}
                                              className="inline-flex items-center justify-center p-1.5 rounded-md text-red-600 hover:bg-red-50 transition-colors"
                                              title="Delete equipment"
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </button>
                                          ))}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>


      {/* Equipment Detail Modal */}
      <EquipmentDetailModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedEquipment(null);
        }}
        equipment={selectedEquipment}
        onUpdate={(id, updates) => {
          // Update the equipment item in the register using the hook
          updateItem(id, updates);
          showAlert('Equipment updated successfully');
        }}
      />


      {/* Delete Reason Modal */}
      <DeleteModal />

      {/* FT-459: Custom dialog hosts — always mounted in the main register
          return so showConfirm/showAlert render immediately (e.g. the
          archive-restore confirmation). Without these the awaited confirm
          hangs and the restore never fires. */}
      <ConfirmDialog />
      <AlertDialog />

      {/* FT-358: mobile bottom-bar actions registered via
          useSetBottomBarActions above — the shell owns the bar. Export
          dropped from list bar per brief §6. */}
    </>
  );
};

// Phase 4 Fix 2.1: Wrap component in React.memo to prevent unnecessary re-renders
export default memo(EquipmentRecordsTab);